import axios from 'axios';
import getApiUrl from 'src/utils/get-api-url';

const apiUrl = getApiUrl();

/**
 * Get all deallocations with optional filters' parameter.
 * 
 * @param {Object} filters 
 * @param {string[]} [filters.role]
 * @param {string[]} [filters.allianceAndGu]
 * @param {string[]} [filters.site]
 * @param {string[]} [filters.client]
 * @param {string[]} [filters.positionMap]
 * @param {string[]} [filters.englishLevel]
 * @param {string[]} [filters.autoDeclaration]
 * @param {string[]} [filters.skills]
 * @param {number} [filters.cutoffPercent]
 * @param {string[]} [filters.cutoffDate]
 * @returns {Promise<Object>}
 */
export function list(filters = {}, paginationOptions = {}) {
  const params = {
    ...filters,
    limit: paginationOptions.limit || 10,
    page: paginationOptions.currentPage || 1
  };
  
  return axios.get(`${apiUrl}/deallocation`, {
    params
  });
}

/**
 * Get all deallocations with fewer attributes and associations for needs' page.
 *
 * @param {Object} [queryParameters={}] 
 * @param {Object} [paginationOptions={}] 
 * @returns {Promise<Object>}
 */
export function listForNeeds(queryParameters = {}, paginationOptions = {}) {
  const params = {
    ...queryParameters,
    limit: paginationOptions.limit || 10,
    currentPage: paginationOptions.currentPage || 1
  };
  return axios.get(`${apiUrl}/deallocation/collaborator`, {
    params
  });
}

/**
 * Post a new manually created deallocation.
 * 
 * @param {Object} deallocation
 * @param {string} deallocation.login
 * @param {string} deallocation.name
 * @param {string} deallocation.positionMap
 * @param {string} deallocation.role
 * @param {string} deallocation.englishLevel
 * @param {string} [deallocation.alliance]
 * @param {string} [deallocation.businessPartner]
 * @param {string} [deallocation.cLob]
 * @param {string} [deallocation.client]
 * @param {string} [deallocation.description]
 * @param {string} [deallocation.contractType]
 * @param {string} [deallocation.gu]
 * @param {string} [deallocation.pdm]
 * @param {string} [deallocation.personWithDisability]
 * @param {string} [deallocation.site]
 * @param {Object} deallocation.skills - default: object with empty attributes
 * @param {string} [deallocation.skills.id='']
 * @param {string} [deallocation.skills.skill='']
 * @param {string} [deallocation.skills.mainSkill='N']
 * @param {number} [deallocation.skills.rating='']
 * @returns {Promise<Object>}
 */
export function create(deallocation) {
  return axios.post(`${apiUrl}/deallocation`, deallocation);
}

/**
 * Update n attributes of a manually created deallocation.
 * 
 * @param {Object} deallocation
 * @param {string} deallocation.id - id from the deallocation to be updated
 * @param {string} deallocation.login
 * @param {string} deallocation.name
 * @param {string} deallocation.positionMap
 * @param {string} deallocation.role
 * @param {string} deallocation.englishLevel
 * @param {string} [deallocation.alliance]
 * @param {string} [deallocation.businessPartner]
 * @param {string} [deallocation.cLob]
 * @param {string} [deallocation.client]
 * @param {string} [deallocation.description]
 * @param {string} [deallocation.contractType]
 * @param {string} [deallocation.gu]
 * @param {string} [deallocation.pdm]
 * @param {string} [deallocation.personWithDisability]
 * @param {string} [deallocation.site]
 * @param {Object} deallocation.skills - default: object with empty attributes
 * @param {string} [deallocation.skills.id='']
 * @param {string} [deallocation.skills.skill='']
 * @param {string} [deallocation.skills.mainSkill='N']
 * @param {number} [deallocation.skills.rating='']
 * @returns {Promise<Object>}
 */
export function update(deallocation) {
  return axios.put(`${apiUrl}/deallocation/${deallocation.id}`, deallocation);
}

/**
 * Deactivate a manually created deallocation.
 * 
 * @param {string} deallocationId - id from the deallocation to be deactivated
 * @returns {Promise<Object>}
 */
export function remove(deallocationId) {
  return axios.delete(`${apiUrl}/deallocation/${deallocationId}`);
}